import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "commoniq_token";
const REFRESH_KEY = "commoniq_refresh";

// In-memory token cache (primary). localStorage acts as a survival fallback
// so a page reload keeps the session — while satisfying the ingress wildcard
// CORS constraint that breaks credentialed httpOnly cookies. On refresh,
// AuthContext still calls /auth/refresh to trade the refresh token for a
// fresh short-lived access token.
let inMemoryAccess = null;
let inMemoryRefresh = null;

const api = axios.create({
  baseURL: `${BASE}/api`,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const t = inMemoryAccess || localStorage.getItem(TOKEN_KEY);
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

export const setAuthToken = (token) => {
  inMemoryAccess = token || null;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};

export const getAuthToken = () => inMemoryAccess || localStorage.getItem(TOKEN_KEY);

export const setRefreshToken = (token) => {
  inMemoryRefresh = token || null;
  if (token) localStorage.setItem(REFRESH_KEY, token);
  else localStorage.removeItem(REFRESH_KEY);
};

export const getRefreshToken = () => inMemoryRefresh || localStorage.getItem(REFRESH_KEY);

// Silent refresh: swap the refresh token for a fresh access token.
// Returns the new access token on success, or null on failure.
export async function attemptRefresh() {
  const rt = getRefreshToken();
  if (!rt) return null;
  try {
    const { data } = await axios.post(
      `${BASE}/api/auth/refresh`,
      { refresh_token: rt },
      { withCredentials: true },
    );
    if (data?.access_token) {
      setAuthToken(data.access_token);
      if (data.refresh_token) setRefreshToken(data.refresh_token);
      return data.access_token;
    }
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.warn("Silent refresh failed", err?.response?.status);
  }
  setAuthToken(null);
  setRefreshToken(null);
  return null;
}

// 401 handler: try silent refresh once, then replay the failed request.
// If refresh also fails, clear tokens so AuthContext can redirect to /login.
let refreshing = null;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const isAuthCall =
      original?.url?.includes("/auth/login") ||
      original?.url?.includes("/auth/register") ||
      original?.url?.includes("/auth/refresh");

    if (status === 401 && !original?._retry && !isAuthCall) {
      original._retry = true;
      refreshing = refreshing || attemptRefresh();
      const newToken = await refreshing;
      refreshing = null;
      if (newToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      }
      if (!path.endsWith("/login") && !path.endsWith("/register")) {
        setAuthToken(null);
        setRefreshToken(null);
      }
    }
    return Promise.reject(error);
  },
);

export default api;
export const API_BASE = `${BASE}/api`;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
